import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

export interface Series {
  name: string;
  data: number[];
  color: string;
  area?: boolean;
}

export function SparkChart({
  x,
  series,
  height = 160,
  unit = '',
  decimals = 1,
  type = 'line',
}: {
  x: string[];
  series: Series[];
  height?: number;
  unit?: string;
  decimals?: number;
  type?: 'line' | 'bar';
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inst = useRef<echarts.ECharts | null>(null);

  const option = useMemo(() => {
    // Smart decimals: if all values are tiny (< 1), show more precision; if large, fewer.
    const allValues = series.flatMap((s) => s.data).filter((v) => Number.isFinite(v));
    const maxVal = allValues.length ? Math.max(...allValues) : 0;
    const yDecimals = maxVal === 0 ? 0 : maxVal < 1 ? 3 : maxVal < 10 ? 2 : maxVal < 100 ? 1 : 0;
    return {
      animation: false,
      // containLabel makes echarts auto-size the gutters so axis labels never get clipped
      grid: { left: 6, right: 8, top: 12, bottom: 6, containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(0,0,0,0.85)',
        borderWidth: 0,
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: Array<{ axisValueLabel: string; data: number; seriesName: string; color: string }>) => {
          const ts = params[0]?.axisValueLabel ?? '';
          const lines = params.map(
            (p) =>
              `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>${p.seriesName}: ${p.data == null ? '-' : p.data.toFixed(decimals)}${unit}`,
          );
          return [`<div style="opacity:0.7;margin-bottom:4px">${ts}</div>`, ...lines].join('<br/>');
        },
      },
      xAxis: {
        type: 'category',
        data: type === 'line' ? x.map(short) : x,
        axisLine: { lineStyle: { color: 'rgba(127,127,127,0.2)' } },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 10,
          color: 'rgba(127,127,127,0.7)',
          interval: type === 'bar' ? 0 : Math.max(0, Math.floor(x.length / 6) - 1),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        // Ensure visible axis even when all data is ~0
        ...(maxVal === 0 ? { max: 1 } : {}),
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: 'rgba(127,127,127,0.12)' } },
        axisLabel: {
          fontSize: 10,
          color: 'rgba(127,127,127,0.7)',
          formatter: (v: number) => `${v.toFixed(yDecimals)}${unit}`,
        },
      },
      series: series.map((s) =>
        type === 'bar'
          ? {
              name: s.name,
              type: 'bar',
              stack: 'total',
              data: s.data,
              itemStyle: { color: s.color, borderRadius: [4, 4, 0, 0] },
              barMaxWidth: 28,
            }
          : {
              name: s.name,
              type: 'line',
              smooth: true,
              showSymbol: false,
              sampling: 'lttb',
              data: s.data,
              lineStyle: { color: s.color, width: 2 },
              itemStyle: { color: s.color },
              ...(s.area && {
                areaStyle: {
                  color: {
                    type: 'linear',
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: hexA(s.color, 0.28) },
                      { offset: 1, color: hexA(s.color, 0) },
                    ],
                  },
                },
              }),
            },
      ),
    };
  }, [x, series, unit, decimals, type]);

  useEffect(() => {
    if (!ref.current) return;
    if (!inst.current) inst.current = echarts.init(ref.current);
    inst.current.setOption(option, true);
    const ro = new ResizeObserver(() => inst.current?.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
    };
  }, [option]);

  useEffect(
    () => () => {
      inst.current?.dispose();
      inst.current = null;
    },
    [],
  );

  return <div ref={ref} style={{ width: '100%', height }} />;
}

function short(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function hexA(hex: string, a: number): string {
  if (hex.startsWith('#') && hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  return hex;
}
